import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircle, faCircleCheck, faCircleHalfStroke, faCircleXmark, faWarning} from "@fortawesome/free-solid-svg-icons";
import React from "react";

export const CUSTOMER_FIGURE_STATUS_NOTHING = 'nothing';
export const CUSTOMER_FIGURE_STATUS_NOT_ACHIEVED = 'not_achieved';
export const CUSTOMER_FIGURE_STATUS_IN_PROGRESS = 'in_progress';
export const CUSTOMER_FIGURE_STATUS_ACHIEVED = 'achieved';
export const CUSTOMER_FIGURE_STATUS_CONSISTENTLY_ACHIEVED = 'consistently_achieved';
export const DEFAULT_EXAM_REQUIREMENT_MIN_STATUS = CUSTOMER_FIGURE_STATUS_ACHIEVED;
export const CUSTOMER_STATUS_ACTIVE = 'active';
export const CUSTOMER_STATUS_INACTIVE = 'inactive';
export const USER_TYPE_SUPER_ADMIN = 'super_admin';
export const USER_TYPE_TEACHER = 'teacher';
export const BOOKING_STATUS_ACTIVE = 'onlyActive';
export const BOOKING_STATUS_CANCELLED = 'onlyCancelled';

export const EVENT_TYPE_CLASS = 'class';
export const EVENT_TYPE_SPECIAL_EVENT = 'special_event';

export const PRODUCT_TYPE_CREDIT_PASS = 'credit_pass';
export const PRODUCT_TYPE_EVENT_TICKET = 'event_ticket';

export const PAYMENT_STATUS_PENDING = 'pending';
export const PAYMENT_STATUS_PAID = 'paid';
export const PAYMENT_STATUS_CANCELLED = 'cancelled';
export const PAYMENT_STATUS_FAILED = 'failed';
export const PAYMENT_STATUS_PROCESSING = 'processing';

// The backend sends payment methods as their internal snake_case name
// (OrderJsonView / PaymentJsonView / PayoutTransactionJsonView all use
// PaymentMethod::getName()), never the customer-facing display label.
export const PAYMENT_METHOD_PAYPAL = 'paypal';
export const PAYMENT_METHOD_PAYPAL_MANUAL = 'paypal_manual';
export const PAYMENT_METHOD_CREDIT_CARD = 'credit_card';
export const PAYMENT_METHOD_DIRECT_DEBIT = 'direct_debit';
export const PAYMENT_METHOD_CASH = 'cash';
export const PAYMENT_METHOD_BANK_TRANSFER = 'bank_transfer';
export const PAYMENT_METHOD_PAY_BY_BANK = 'pay_by_bank';
export const PAYMENT_METHOD_MOLLIE_PAY_BY_BANK = 'mollie_pay_by_bank';

export const EVENT_GROUP_STATUS_ACTIVE = 'active';
export const EVENT_GROUP_STATUS_ARCHIVED = 'archived';

export const EVENT_SERIES_STATUS_ACTIVE = 'active';
export const EVENT_SERIES_STATUS_CANCELLED = 'cancelled';

export const EVENT_STATUS_ACTIVE = 'active';
export const EVENT_STATUS_CANCELLED = 'cancelled';

export const PRODUCT_STATUS_ACTIVE = 'active';
export const PRODUCT_STATUS_ARCHIVED = 'archived';

export const PRODUCT_SOLD_OUT_ERROR_CODE = 703;
export const ITEM_VARIANT_STOCK_CHANGED_ERROR_CODE = 1312;
export const ITEM_VARIANT_PROPERTY_DUPLICATED_ERROR_CODE = 1314;

export const ORDER_STATUS_CONFIRMED = 'confirmed';
export const ORDER_SOURCE_WEBSHOP = 'webshop';

export const getCustomerFigureDisplayStatus = (status) => {
    switch (status) {
        case CUSTOMER_FIGURE_STATUS_ACHIEVED:
            return <FontAwesomeIcon icon={faCircleCheck} style={{color : 'green'}}/>;
        case CUSTOMER_FIGURE_STATUS_IN_PROGRESS:
            return <FontAwesomeIcon icon={faCircleHalfStroke} style={{color : 'orange'}}/>;
        case CUSTOMER_FIGURE_STATUS_NOT_ACHIEVED:
            return <FontAwesomeIcon icon={faCircleXmark} style={{color : 'red'}}/>;
        case CUSTOMER_FIGURE_STATUS_CONSISTENTLY_ACHIEVED:
            return <FontAwesomeIcon icon={faCircleCheck} style={{color : 'blue'}}/>;
        default:
            return <FontAwesomeIcon icon={faCircle} style={{color : 'lightgrey'}}/>;
    }
}

export const getNeedToLevelIcon = () => {
    return <FontAwesomeIcon icon={faWarning} style={{color : "#ff0000",}}/>;
}

export const readyToNextLevelIcon = <FontAwesomeIcon icon={faCircleCheck} style={{color : 'green'}}/>;
export const notReadyToNextLevelIcon = <FontAwesomeIcon icon={faCircleXmark} style={{color : 'red'}}/>;

export const getCustomerFigureStatuses = () => {
    return [
        {id : CUSTOMER_FIGURE_STATUS_NOTHING, name : 'Nothing'},
        {id : CUSTOMER_FIGURE_STATUS_NOT_ACHIEVED, name : 'No'},
        {id : CUSTOMER_FIGURE_STATUS_IN_PROGRESS, name : 'Pending'},
        {id : CUSTOMER_FIGURE_STATUS_ACHIEVED, name : 'Yes'},
        {id : CUSTOMER_FIGURE_STATUS_CONSISTENTLY_ACHIEVED, name : 'Excellent'}
    ]
}

export const getBookingStatuses = () => {
    return [
        {id : BOOKING_STATUS_ACTIVE, name : 'Active'},
        {id : BOOKING_STATUS_CANCELLED, name : 'Cancelled'}
    ]
}

const formatStatusLabel = (status) => {
    const normalized = String(status).toLowerCase();

    return normalized.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

export const getOrderStatusBadge = (status) => {
    if (!status) {
        return null;
    }

    const normalized = String(status).toLowerCase();

    // NOTE: the exact order status enum isn't finalized yet on the backend
    // (order-status-state-machine branch), so colors are keyword-matched
    // rather than an exhaustive switch. Unknown values fall back to gray.
    let colorClasses = 'bg-gray-100 text-gray-700';
    if (normalized.includes('cancel')) {
        colorClasses = 'bg-red-100 text-red-700';
    } else if (normalized.includes('pick') || normalized.includes('complete') || normalized.includes('fulfilled')) {
        colorClasses = 'bg-green-100 text-green-700';
    } else if (normalized.includes('paid')) {
        colorClasses = 'bg-blue-100 text-blue-700';
    }

    return (
        <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${colorClasses}`}>
            {formatStatusLabel(status)}
        </span>
    );
};

// Payment status is a closed enum on the backend (PaymentStatus), so unlike the
// order status these are matched exactly. "Processing" needs its own color: with
// Mollie Pay by Bank a SEPA transfer takes 2-3 working days to settle, so orders
// sit in it routinely and the admin must be able to tell "money is on the way"
// apart from "money arrived".
const PAYMENT_STATUS_BADGE_COLORS = {
    [PAYMENT_STATUS_PAID]       : 'border-green-300 text-green-600',
    [PAYMENT_STATUS_PROCESSING] : 'border-blue-300 text-blue-600 bg-blue-50',
    [PAYMENT_STATUS_PENDING]    : 'border-amber-300 text-amber-600',
    [PAYMENT_STATUS_FAILED]     : 'border-red-400 text-red-700 bg-red-50 font-semibold',
    [PAYMENT_STATUS_CANCELLED]  : 'border-gray-300 text-gray-500',
};

export const getPaymentStatusBadge = (status) => {
    if (!status) {
        return null;
    }

    const normalized = String(status).toLowerCase();

    // Deliberately lighter-weight (outline, not filled) than getOrderStatusBadge,
    // so payment status reads as secondary info next to the order status badge.
    const colorClasses = PAYMENT_STATUS_BADGE_COLORS[normalized] ?? 'border-gray-300 text-gray-500';

    const label = normalized === PAYMENT_STATUS_PROCESSING
        ? 'Processing (in transit)'
        : formatStatusLabel(status);

    return (
        <span className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-medium border bg-transparent ${colorClasses}`}>
            {label}
        </span>
    );
};

export const getPaymentStatuses = () => {
    return [
        {id : PAYMENT_STATUS_PENDING, name : 'Pending'},
        {id : PAYMENT_STATUS_PROCESSING, name : 'Processing (in transit)'},
        {id : PAYMENT_STATUS_PAID, name : 'Paid'},
        {id : PAYMENT_STATUS_FAILED, name : 'Failed'},
        {id : PAYMENT_STATUS_CANCELLED, name : 'Cancelled'},
    ];
};

// Plain-text counterpart of getPaymentStatusBadge, for places that cannot
// render markup - <option> labels, for instance.
export const getPaymentStatusLabel = (status) => {
    if (!status) {
        return null;
    }

    const normalized = String(status).toLowerCase();
    const known = getPaymentStatuses().find((paymentStatus) => paymentStatus.id === normalized);

    return known ? known.name : formatStatusLabel(status);
};

// Both Stripe and Mollie sell the same customer-facing "Pay by Bank", so the
// backend's display names collide by design. Admin screens need to tell the two
// providers apart (invoice ZIP bucketing and revenue reporting follow the
// provider), so the label is resolved here from the internal name instead.
const PAYMENT_METHOD_LABELS = {
    [PAYMENT_METHOD_PAYPAL]             : 'PayPal',
    [PAYMENT_METHOD_PAYPAL_MANUAL]      : 'PayPal Manual',
    [PAYMENT_METHOD_CREDIT_CARD]        : 'Credit Card (Stripe)',
    [PAYMENT_METHOD_DIRECT_DEBIT]       : 'SEPA Direct Debit (Stripe)',
    [PAYMENT_METHOD_CASH]               : 'Cash',
    [PAYMENT_METHOD_BANK_TRANSFER]      : 'Bank Transfer',
    [PAYMENT_METHOD_PAY_BY_BANK]        : 'Pay by Bank (Stripe)',
    [PAYMENT_METHOD_MOLLIE_PAY_BY_BANK] : 'Pay by Bank (Mollie)',
};

export const getPaymentMethodLabel = (paymentMethod) => {
    if (!paymentMethod) {
        return null;
    }

    const normalized = String(paymentMethod).toLowerCase();

    return PAYMENT_METHOD_LABELS[normalized] ?? formatStatusLabel(paymentMethod);
};

// A failed payment never cancels its order: the booking stays confirmed on
// purpose so nobody loses a reserved spot days later, which leaves these orders
// needing manual follow-up. With Mollie's asynchronous settlement they are the
// ones an admin actually has to chase.
export const needsPaymentFollowUp = (order) => {
    if (!order) {
        return false;
    }

    return String(order.status).toLowerCase() === ORDER_STATUS_CONFIRMED
        && String(order.paymentStatus).toLowerCase() === PAYMENT_STATUS_FAILED;
};

export const MOLLIE_TRANSACTION_TYPE_PAYMENT = 'payment';
export const MOLLIE_TRANSACTION_TYPE_REFUND = 'refund';
export const MOLLIE_TRANSACTION_TYPE_CHARGEBACK = 'chargeback';

// Mollie only attributes a fee to a settlement line when it can match it to a
// single balance transaction; otherwise the field comes back null on purpose.
// This is accounting data, so an unknown amount stays visibly empty - rendering
// a made-up "0.00" would read as "no fee was charged", which is a different claim.
// Refunds and chargebacks likewise arrive as negative amounts and must stay
// negative: the lines only add up to the settlement total if the signs survive.
export const getSettlementAmountCell = (amount, currency) => {
    if (amount === null || amount === undefined) {
        return <span className="text-gray-300">—</span>;
    }

    return (
        <span className={amount < 0 ? 'text-red-600' : undefined}>
            {amount + ' ' + currency}
        </span>
    );
};

const MOLLIE_TRANSACTION_TYPE_COLORS = {
    [MOLLIE_TRANSACTION_TYPE_PAYMENT]    : 'bg-green-100 text-green-700',
    [MOLLIE_TRANSACTION_TYPE_REFUND]     : 'bg-amber-100 text-amber-700',
    [MOLLIE_TRANSACTION_TYPE_CHARGEBACK] : 'bg-red-100 text-red-700',
};

export const getMollieTransactionTypeBadge = (type) => {
    if (!type) {
        return null;
    }

    const normalized = String(type).toLowerCase();
    const colorClasses = MOLLIE_TRANSACTION_TYPE_COLORS[normalized] ?? 'bg-gray-100 text-gray-700';

    return (
        <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${colorClasses}`}>
            {formatStatusLabel(type)}
        </span>
    );
};

// A settlement that has not been paid out yet has no settledAt date, and
// convertToDisplayDate would turn that null into "1. Januar 1970".
export const convertToOptionalDisplayDate = (date) => {
    if (!date) {
        return <span className="text-gray-300">—</span>;
    }

    return convertToDisplayDate(date);
};

export const getCustomerFigureStatusNameById = (id) => {
    const customerFigureStatuses = getCustomerFigureStatuses();
    const status = customerFigureStatuses.find(status => status.id === id);

    return status ? status.name : null;
};

// A product's maxSellableQuantity caps how many of it can ever be sold across
// all customers: null means unlimited, 0 means "none available right now".
// soldQuantity is how many are already taken.
export const isProductSoldOut = (product) => {
    if (!product || product.maxSellableQuantity === null || product.maxSellableQuantity === undefined) {
        return false;
    }

    return product.soldQuantity >= product.maxSellableQuantity;
};

export const formatProductSoldQuantity = (product) => {
    if (!product || product.soldQuantity === null || product.soldQuantity === undefined) {
        return '-';
    }

    if (product.maxSellableQuantity === null || product.maxSellableQuantity === undefined) {
        return product.soldQuantity + ' / unlimited';
    }

    return product.soldQuantity + ' / ' + product.maxSellableQuantity;
};

export const handleErrorResponse = async (response, message) => {
    if (!response.ok) {
        if (response.status === 401) {
            localStorage.removeItem('ACCESS_TOKEN')
        }

        // A server error page, an empty body or a JSON null carries no message;
        // the caller's message stands in for it.
        const responseJson = (await response.json().catch(() => null)) || {};

        const error = new Error(responseJson.message || message);
        error.code = responseJson.code;
        error.status = response.status;
        throw error;
    }
}

export const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        return parts.pop().split(';').shift();
    }
}

export const API_URL = process.env.REACT_APP_API_URL;

export const convertToDisplayDateTime = (dateTime) => {
    const dt = new Date(dateTime);

    return dt.toLocaleString('de-DE', {
        year   : 'numeric',
        month  : 'long',
        day    : 'numeric',
        hour   : 'numeric',
        minute : 'numeric',
        hour12 : false,
    });
}

export const convertToDisplayDate = (date) => {
    const d = new Date(date);

    return d.toLocaleString('de-DE', {
        year  : 'numeric',
        month : 'long',
        day   : 'numeric'
    });
}

// <input type="datetime-local"> only accepts a "T"-separated local date-time
// with no timezone designator, but the backend isn't consistent about what
// it sends: some endpoints use "Y-m-d H:i(:s)" (space-separated, no zone),
// others send full ISO 8601 with a "Z" or "+02:00" offset. A space makes the
// browser treat the value as invalid; a trailing zone makes it unparseable
// (the field just renders blank) — so this normalizes both away.
export const toDatetimeLocalValue = (dateTime) => {
    if (!dateTime) {
        return null;
    }

    return String(dateTime)
        .replace(' ', 'T')
        .replace(/(Z|[+-]\d{2}:?\d{2})$/, '');
}

export const formatEventDate = (startedAt, endedAt) => {
    const startDate = new Date(startedAt);
    const endDate = new Date(endedAt);

    // Format: Day Shortname, Day Month Year
    const dateFormatter = new Intl.DateTimeFormat("en-US", {
        weekday : "short",
        day     : "2-digit",
        month   : "short",
        year    : "numeric",
    });
    const timeFormatter = new Intl.DateTimeFormat("en-US", {
        hour   : "2-digit",
        minute : "2-digit",
        hour12 : false,
    });

    const datePart = dateFormatter.format(startDate); // e.g., "Wed 08 Jan 2025"
    const startTime = timeFormatter.format(startDate); // e.g., "18:30"
    const endTime = timeFormatter.format(endDate); // e.g., "20:30"

    return `${datePart} - ${startTime} - ${endTime}`;
}

export const createNowDate = () => {
    return new Date().toISOString().substr(0, 10);
}

export const getCustomerStatuses = () => {
    return [
        CUSTOMER_STATUS_ACTIVE,
        CUSTOMER_STATUS_INACTIVE
    ];
}

export const convertToYmdHis = (date) => {
    const d = new Date(date);
    return d.toISOString().slice(0, 19).replace('T', ' ');
}